import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
    makeStyles,
    Typography,
    Paper,
    Grid,
    Box,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@material-ui/core";
import { ToggleButton, ToggleButtonGroup } from "@material-ui/lab";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";
import { toast } from "react-toastify";
import ImageUploadField from "../../common/ImageUploadField";

const useStyles = makeStyles((theme) => ({
    content: {
        padding: theme.spacing(3),
    },
    header: {
        marginBottom: theme.spacing(3),
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    backButton: {
        marginRight: theme.spacing(2),
    },
    formCard: {
        padding: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
    buttonGroup: {
        display: "flex",
        gap: theme.spacing(2),
        marginTop: theme.spacing(3),
    },
    fileInput: {
        width: "100%",
        marginBottom: "12px",
        "& .MuiInputLabel-outlined": {
            transform: "translate(14px, 13px)",
        },
        "& .MuiOutlinedInput-adornedEnd": {
            height: "40px",
            paddingRight: "5px",
        },
        "& input": {
            fontSize: "13px",
        },
    },
    sectionText: {
        fontSize: "13px",
        fontWeight: 700,
        paddingLeft: "4px",
    },
    input: {
        width: "100%",
        "& button": {
            fontSize: "13px",
            lineHeight: "15px",
            width: "50%",
        },
    },
    localizationItem: {
        border: "1px solid #cccccc",
        borderRadius: "4px",
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        backgroundColor: "#f9f9f9",
    },
    localizationHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
}));

const PartnerBenefitEditingPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const { setLoading } = useLoading();
    
    const isEditMode = !!id;
    
    const [formData, setFormData] = useState({
        code: "",
        logoPath: "",
        image1Path: "",
        image2Path: "",
        image3Path: "",
        url: "",
        status: "ACTIVE",
    });

    const [errors, setErrors] = useState({});
    const [localizations, setLocalizations] = useState([]);

    // Load existing partner data if in edit mode
    useEffect(() => {
        if (isEditMode) {
            loadPartnerData();
        }
    }, [id]);

    const loadPartnerData = async () => {
        setLoading(true);
        try {
            const response = await apiHelper.cineclub.getPartnerById(id);
            if (response.data) {
                const partner = response.data;
                setFormData({
                    code: partner.code || "",
                    logoPath: partner.logoPath || "",
                    image1Path: partner.image1Path || "",
                    image2Path: partner.image2Path || "",
                    image3Path: partner.image3Path || "",
                    url: partner.url || "",
                    status: partner.status || "ACTIVE",
                });

                // Load localizations
                if (partner.localizations) {
                    const locs = partner.localizations;
                    const enLocs = locs.en || [];
                    const tcLocs = locs.tc || [];

                    // Match by index position (allows multiple items of same type)
                    const maxLength = Math.max(enLocs.length, tcLocs.length);
                    const localizationArray = [];

                    for (let i = 0; i < maxLength; i++) {
                        const enLoc = enLocs[i];
                        const tcLoc = tcLocs[i];

                        // Use the type from en if available, otherwise from tc, default to "title"
                        const type = (enLoc && enLoc.type) || (tcLoc && tcLoc.type) || "title";

                        localizationArray.push({
                            type,
                            valueEn: (enLoc && enLoc.value) || "",
                            valueTc: (tcLoc && tcLoc.value) || "",
                        });
                    }

                    setLocalizations(localizationArray);
                } else {
                    setLocalizations([]);
                }
            }
        } catch (error) {
            console.error("Error loading partner data:", error);
            alert("Failed to load partner data. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const handleStatusChange = (event, newValue) => {
        if (newValue !== null) {
            setFormData(prev => ({
                ...prev,
                status: newValue
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.code.trim()) {
            newErrors.code = "Code is required";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            // Extract filename from image URLs (remove path, keep only filename)
            const extractFilename = (url) => {
                if (!url || !url.trim()) return null;
                const trimmed = url.trim();
                // If it's already just a filename (no slashes), return as is
                if (!trimmed.includes("/")) return trimmed;
                // Extract filename from URL
                return trimmed.split("/").pop();
            };

            // Transform localizations to the required format
            const transformedLocalizations = {};
            if (localizations.length > 0) {
                const enLocs = [];
                const tcLocs = [];

                localizations.forEach((loc) => {
                    if (loc.valueEn && loc.valueEn.trim()) {
                        enLocs.push({
                            type: loc.type,
                            value: loc.valueEn.trim(),
                        });
                    }
                    if (loc.valueTc && loc.valueTc.trim()) {
                        tcLocs.push({
                            type: loc.type,
                            value: loc.valueTc.trim(),
                        });
                    }
                });

                if (enLocs.length > 0) {
                    transformedLocalizations.en = enLocs;
                }
                if (tcLocs.length > 0) {
                    transformedLocalizations.tc = tcLocs;
                }
            }

            const payload = {
                code: formData.code.trim(),
                logoPath: extractFilename(formData.logoPath) || null,
                image1Path: extractFilename(formData.image1Path) || null,
                image2Path: extractFilename(formData.image2Path) || null,
                image3Path: extractFilename(formData.image3Path) || null,
                url: formData.url.trim() || null,
                status: formData.status,
                localizations: Object.keys(transformedLocalizations).length > 0 ? transformedLocalizations : null,
            };

            let response;
            if (isEditMode) {
                response = await apiHelper.cineclub.updatePartner(id, payload);
            } else {
                response = await apiHelper.cineclub.createPartner(payload);
            }
            
            // Log the response to debug
            console.log("API Response:", response);
            
            if (response.data && response.data.code === 0) {
                // Success - show toast and redirect back to listing page
                const action = isEditMode ? "updated" : "created";
                toast.dark(`Partner ${action} successfully.`);
                history.push("/cineclub/membership/partners");
            } else {
                // Handle error response
                const action = isEditMode ? "update" : "create";
                console.error(`Failed to ${action} partner:`, response);
                alert(`Failed to ${action} partner. Please try again.`);
            }
        } catch (error) {
            const action = isEditMode ? "updating" : "creating";
            console.error(`Error ${action} partner:`, error);
            alert(`An error occurred while ${action} the partner. Please try again.`);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        history.push("/cineclub/membership/partners");
    };

    const handleFileUpload = async (field, file) => {
        if (!file) return;

        try {
            setLoading(true);
            const result = await apiHelper.cineclub.uploadMembershipFile(file, "HOMEPAGE");
            const url = result.data.url;
            // Store the full URL for display, we'll extract filename when submitting
            setFormData(prev => ({
                ...prev,
                [field]: url
            }));
        } catch (error) {
            console.error("Error uploading file:", error);
            alert("Failed to upload image. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleImageDelete = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: ""
        }));
    };

    const handleAddLocalization = () => {
        // Check if title already exists
        const hasTitle = localizations.some(loc => loc.type === "title");

        setLocalizations(prev => [
            ...prev,
            {
                type: hasTitle ? "content" : "title",
                valueEn: "",
                valueTc: "",
            }
        ]);
    };

    const handleRemoveLocalization = (index) => {
        setLocalizations(prev => prev.filter((_, i) => i !== index));
    };

    const handleLocalizationChange = (index, field, value) => {
        // Prevent changing to "title" if another title already exists
        if (field === "type" && value === "title") {
            const hasOtherTitle = localizations.some((loc, i) => i !== index && loc.type === "title");
            if (hasOtherTitle) {
                alert("Only one title localization is allowed. Please remove the existing title first.");
                return;
            }
        }

        setLocalizations(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: value
            };
            return updated;
        });
    };

    return (
        <div className={classes.content}>
            <Box className={classes.header}>
                <Button
                    className={classes.backButton}
                    startIcon={<ArrowBackIcon />}
                    onClick={handleCancel}
                >
                    Back to Partners
                </Button>
                <Typography variant="h4" component="h1">
                    {isEditMode ? "Edit Partner Benefit" : "Create Partner Benefit"}
                </Typography>
            </Box>

            <Paper className={classes.formCard}>
                <Typography variant="h6" gutterBottom>
                    Partner Details
                </Typography>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Code *"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.code}
                            onChange={handleInputChange("code")}
                            error={!!errors.code}
                            helperText={errors.code}
                            required
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="URL"
                            variant="outlined"
                            className={classes.formField}
                            value={formData.url}
                            onChange={handleInputChange("url")}
                            helperText="Optional URL for the partner"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ImageUploadField
                            field="logoPath"
                            value={formData.logoPath}
                            onUpload={handleFileUpload}
                            onDelete={handleImageDelete}
                            label="Logo Path"
                            required={false}
                            classes={classes}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ImageUploadField
                            field="image1Path"
                            value={formData.image1Path}
                            onUpload={handleFileUpload}
                            onDelete={handleImageDelete}
                            label="Image 1 Path"
                            required={false}
                            classes={classes}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ImageUploadField
                            field="image2Path"
                            value={formData.image2Path}
                            onUpload={handleFileUpload}
                            onDelete={handleImageDelete}
                            label="Image 2 Path"
                            required={false}
                            classes={classes}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ImageUploadField
                            field="image3Path"
                            value={formData.image3Path}
                            onUpload={handleFileUpload}
                            onDelete={handleImageDelete}
                            label="Image 3 Path"
                            required={false}
                            classes={classes}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box className={classes.formField}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
                                <Typography variant="h6" gutterBottom style={{ margin: 0 }}>
                                    Descriptions
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddLocalization}
                                >
                                    Add Description
                                </Button>
                            </Box>

                            {localizations.map((localization, index) => {
                                // Check if another title exists (excluding current item)
                                const hasOtherTitle = localizations.some((loc, i) => i !== index && loc.type === "title");

                                return (
                                <Box key={index} className={classes.localizationItem}>
                                    <Box className={classes.localizationHeader}>
                                        <FormControl
                                            variant="outlined"
                                            style={{ minWidth: 150 }}
                                        >
                                            <InputLabel>Type</InputLabel>
                                            <Select
                                                value={localization.type}
                                                onChange={(e) => handleLocalizationChange(index, "type", e.target.value)}
                                                label="Type"
                                            >
                                                <MenuItem value="title" disabled={hasOtherTitle}>
                                                    Title {hasOtherTitle && "(Only one allowed)"}
                                                </MenuItem>
                                                <MenuItem value="content">Content</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Button
                                            variant="outlined"
                                            color="secondary"
                                            size="small"
                                            startIcon={<DeleteIcon />}
                                            onClick={() => handleRemoveLocalization(index)}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                fullWidth
                                                label="English Value"
                                                variant="outlined"
                                                value={localization.valueEn}
                                                onChange={(e) => handleLocalizationChange(index, "valueEn", e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={12}>
                                            <TextField
                                                fullWidth
                                                label="Traditional Chinese Value"
                                                variant="outlined"
                                                value={localization.valueTc}
                                                onChange={(e) => handleLocalizationChange(index, "valueTc", e.target.value)}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            );
                            })}

                            {localizations.length === 0 && (
                                <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                                    No localizations added. Click "Add Description" to add one.
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box className={classes.formField}>
                            <Typography variant="body2" className={classes.sectionText} style={{ marginBottom: "8px" }}>
                                Status
                            </Typography>
                            <ToggleButtonGroup
                                value={formData.status}
                                onChange={handleStatusChange}
                                exclusive
                                aria-label="Status"
                                className={classes.input}
                            >
                                <ToggleButton
                                    value="ACTIVE"
                                    aria-label="Active"
                                    style={{ width: "50%" }}
                                >
                                    Active
                                </ToggleButton>
                                <ToggleButton
                                    value="INACTIVE"
                                    aria-label="Inactive"
                                    style={{ width: "50%" }}
                                >
                                    Inactive
                                </ToggleButton>
                            </ToggleButtonGroup>
                            {errors.status && (
                                <Typography variant="caption" color="error" style={{ marginTop: "8px", display: "block" }}>
                                    {errors.status}
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </Grid>

                <Box className={classes.buttonGroup}>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSubmit}
                    >
                        {isEditMode ? "Update" : "Create"}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </Box>
            </Paper>
        </div>
    );
};

export default PartnerBenefitEditingPage;

